import { Alert, Input, Select, SelectItem, Slider } from "@heroui/react";
import { LinkIcon } from "lucide-react";
import { toast } from "react-toastify";

import { CanvasCore } from "@/lib/canvas";
import { WebGLUtils } from "@/lib/webgl";

import { useAppContext } from "../../contexts/AppContext";
import { useTemplateContext } from "../../contexts/TemplateContext";
import { Button } from "../core/Button";
import { Switch } from "../core/Switch";

export const TemplateSettings = () => {
  const {
    enable,
    setEnable,
    url,
    setURL,
    width,
    setWidth,
    x,
    setX,
    y,
    setY,
    opacity,
    setOpacity,
    style,
    setStyle,
    showMobileTools,
    setShowMobileTools,
  } = useTemplateContext();
  const { settings } = useAppContext();

  return (
    <div id="template-settings" className="flex flex-col p-2">
      <header className="flex flex-col gap-2">
        {!WebGLUtils.detectWebGL() && (
          <Alert color="warning">
            <strong>WebGL is not available</strong>
            <br />
            <span>Templating uses WebGL</span>
          </Alert>
        )}
        <div className="flex items-center gap-2">
          <Switch
            isSelected={enable || false}
            onValueChange={(bool: boolean) => {
              setEnable(bool);

              if (bool && x === undefined && y === undefined) {
                const canvas = CanvasCore.get(false);
                if (canvas) {
                  const { canvasX, canvasY } =
                    canvas.panZoomTransformToCanvas();
                  setX(canvasX);
                  setY(canvasY);
                }
              }
            }}
          />
          <h2 className="text-xl">Template</h2>
        </div>
        <p className="text-default-600 text-xs">
          Display an image over the canvas to help guide placing
        </p>
      </header>
      {enable && (
        <section className="flex flex-col gap-2 mt-4">
          <Input
            label="Template URL"
            size="sm"
            value={url || ""}
            onValueChange={setURL}
          />
          <Input
            label="Template Width"
            size="sm"
            type="number"
            min="1"
            max={10_000}
            value={width?.toString() || ""}
            onValueChange={(v) => setWidth(parseInt(v))}
          />
          <div className="flex flex-row gap-1">
            <Input
              label="Template X"
              size="sm"
              type="number"
              value={x?.toString() || ""}
              onValueChange={(v) => setX(parseInt(v))}
            />
            <Input
              label="Template Y"
              size="sm"
              type="number"
              value={y?.toString() || ""}
              onValueChange={(v) => setY(parseInt(v))}
            />
          </div>
          <Slider
            label="Template Opacity"
            step={1}
            minValue={0}
            maxValue={100}
            value={opacity ?? 100}
            onChange={(v) => setOpacity(v as number)}
            getValue={(v) => v + "%"}
          />
          <Select
            label="Template Style"
            selectedKeys={[style]}
            onChange={(e) => setStyle(e.target.value as any)}
          >
            <SelectItem key="SOURCE">Source</SelectItem>
            <SelectItem key="ONE_TO_ONE">One-to-one</SelectItem>
            <SelectItem key="ONE_TO_ONE_INCORRECT">
              One-to-one (keep incorrect)
            </SelectItem>
            <SelectItem key="DOTTED_SMALL">Dotted Small</SelectItem>
            <SelectItem key="DOTTED_BIG">Dotted Big</SelectItem>
            <SelectItem key="SYMBOLS">Symbols</SelectItem>
            <SelectItem key="NUMBERS">Numbers</SelectItem>
          </Select>
          <Switch
            className="md:hidden"
            isSelected={showMobileTools}
            onValueChange={setShowMobileTools}
          >
            Show Mobile Tools
          </Switch>
          {url && (
            <>
              <Switch
                isSelected={settings.get["template.pixelCounts"]}
                onValueChange={(value: boolean) => {
                  settings.set({ key: "template.pixelCounts", value });
                }}
              >
                Show pixels needed of each color
              </Switch>
              <Switch
                isSelected={settings.get["template.hideUnusedColors"]}
                onValueChange={(value: boolean) => {
                  settings.set({ key: "template.hideUnusedColors", value });
                }}
              >
                Hide colors not in template
              </Switch>
            </>
          )}
          {url && x !== undefined && y !== undefined && (
            <Button
              onPress={() => {
                const copied = `${window.location.origin}${window.location.pathname}#tu=${url}${
                  width != null ? `&tw=${width}` : ""
                }&tx=${x}&ty=${y}&ts=${style}&x=${x + (width || 0) / 2}&y=${y + (width || 0) / 2}&zoom=1`;

                navigator.clipboard.writeText(copied);
                toast.success("Link copied to clipboard!");
              }}
              startContent={<LinkIcon aria-hidden="true" size={16} />}
            >
              Copy Shareable Link
            </Button>
          )}
        </section>
      )}
    </div>
  );
};
